<!--
  文件描述：多业态装修模板新建编辑弹窗，支持业态入口锁定默认业态
  @author DIY
  @createTime 2026-07-22
-->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="640px">
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="96px">
      <el-form-item label="名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入模板名称" maxlength="100" show-word-limit />
      </el-form-item>
      <el-form-item label="业态" prop="industry">
        <el-select
          v-model="formData.industry"
          class="w-full"
          :disabled="industryLocked"
          filterable
          placeholder="请选择业态"
          @change="handleIndustryChange"
        >
          <!-- 业态选项：显示图标和名称 -->
          <el-option v-for="item in industries" :key="item.industry" :label="item.name" :value="item.industry">
            <span class="industry-option">
              <Icon :icon="getIndustryIcon(item.industry, item.icon)" />
              <span>{{ item.name }}</span>
            </span>
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="场景" prop="scene">
        <el-select v-model="formData.scene" class="w-full" placeholder="请选择场景">
          <!-- 场景选项：根据业态联动 -->
          <el-option v-for="item in sceneOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="描述" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="3"
          placeholder="请输入模板描述"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
      <el-form-item label="预览图" prop="previewImg">
        <UploadImg
          v-model="formData.previewImg"
          width="120px"
          height="213px"
          :file-size="10"
          directory="diy/template/preview"
        >
          <template #tip>
            <span class="template-preview-tip">建议上传 9:16 竖版图片，用于模板列表和预览弹窗展示</span>
          </template>
        </UploadImg>
      </el-form-item>
      <el-form-item label="平台" prop="platform">
        <el-radio-group v-model="formData.platform">
          <el-radio-button v-for="item in PLATFORM_OPTIONS" :key="item.value" :value="item.value">
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmitForm">确定</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
// ==================== 依赖引入 ====================
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import * as DiyTemplateApi from '@/api/diy/template'
import { UploadImg } from '@/components/UploadFile'
import {
  DEFAULT_SCENE_OPTIONS,
  INDUSTRY_SCENE_OPTIONS,
  PLATFORM_OPTIONS,
  getIndustryIcon
} from './data'

// ==================== 类型定义 ====================
type FormType = 'create' | 'update'

interface TemplateFormProps {
  industries: DiyTemplateApi.DiyIndustryVO[]
  lockedIndustry?: string
}

interface TemplateFormEmits {
  (event: 'success'): void
}

// ==================== Props & Emits ====================
const props = defineProps<TemplateFormProps>()
const emits = defineEmits<TemplateFormEmits>()

// ==================== 响应式数据 ====================
const dialogVisible = ref(false) // 弹窗显示状态
const dialogTitle = ref('新建模板') // 弹窗标题
const formType = ref<FormType>('create') // 当前表单类型
const submitLoading = ref(false) // 提交 loading 状态
const formRef = ref<FormInstance>() // 表单实例
const formData = reactive<DiyTemplateApi.DiyTemplateVO>({
  name: '',
  description: '',
  industry: '',
  scene: 'home',
  platform: 'h5',
  sort: 0
})
const formRules = reactive<FormRules>({
  name: [{ required: true, message: '模板名称不能为空', trigger: 'blur' }],
  industry: [{ required: true, message: '请选择业态', trigger: 'change' }],
  scene: [{ required: true, message: '请选择场景', trigger: 'change' }],
  platform: [{ required: true, message: '请选择平台', trigger: 'change' }]
})

// ==================== 计算属性 ====================
const industryLocked = computed(() => !!props.lockedIndustry && formType.value === 'create')

const sceneOptions = computed(() => {
  return INDUSTRY_SCENE_OPTIONS[formData.industry] || DEFAULT_SCENE_OPTIONS
})

// ==================== 生命周期 ====================

// ==================== 方法 ====================
const resetFormData = (defaultIndustry?: string) => {
  formData.id = undefined
  formData.name = ''
  formData.description = ''
  formData.industry = defaultIndustry || ''
  formData.scene = 'home'
  formData.platform = 'h5'
  formData.previewImg = ''
  formData.sort = 0
}

const open = async (type: FormType, id?: number, defaultIndustry?: string) => {
  resetFormData(defaultIndustry || props.lockedIndustry)
  formType.value = type
  dialogTitle.value = type === 'create' ? '新建模板' : '编辑模板'
  dialogVisible.value = true
  await nextTick()
  formRef.value?.clearValidate()
  if (type === 'update' && id) {
    // 编辑模式需要先加载模板详情再填充表单
    await loadTemplateDetail(id)
  }
}

const loadTemplateDetail = async (id: number) => {
  try {
    const data = await DiyTemplateApi.getDiyTemplate(id)
    Object.assign(formData, data)
  } catch {
    ElMessage.error('模板详情加载失败')
  }
}

const handleIndustryChange = () => {
  const hasScene = sceneOptions.value.some((item) => item.value === formData.scene)
  if (!hasScene) {
    // 当前场景不属于新业态时切换到第一个可用场景
    formData.scene = sceneOptions.value[0]?.value || 'home'
  }
}

const handleSubmitForm = async () => {
  if (!formRef.value) {
    // 表单实例未挂载时不提交
    return
  }
  const valid = await formRef.value.validate()
  if (!valid) {
    // 表单校验失败时不请求接口
    return
  }
  submitLoading.value = true
  try {
    if (formType.value === 'create') {
      // 新建模式调用创建接口
      await DiyTemplateApi.createDiyTemplate(formData)
    } else {
      // 编辑模式调用更新接口
      await DiyTemplateApi.updateDiyTemplate(formData)
    }
    ElMessage.success('保存成功')
    dialogVisible.value = false
    emits('success')
  } catch {
    ElMessage.error('模板保存失败')
  } finally {
    submitLoading.value = false
  }
}

defineExpose({ open })
</script>

<style scoped lang="scss">
.industry-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.template-preview-tip {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
</style>
